import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';

import { nsFormFieldStyles } from '../../../ns-styles/index.js';
import { renderFormField } from '../../../helpers/form-field.js';
import NSJobComponent from '../job.js';
import JobSchema from '../../../models/job-schema.js';
import definition from '@job-schemas/checklist.json';
import { jobSchema } from '../job-schema.js';
import { checklistErrors, checklistItems, configOf } from '../generic-logic.js';
import '../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';

// Work through the node's list and confirm it's done, or say why it can't be
@customElement(NSChecklist.is)
export default class NSChecklist extends NSJobComponent {
  static get is() {
    return 'ns-checklist';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { done: { type: 'hidden' }, cannotComplete: { type: 'hidden' } } });
  }

  @state() private __checked: string[] = [];

  @state() private __confirmed = false;

  @state() private __errors: string[] = [];

  @state() private __stuck = false;

  @state() private __reason = '';

  @state() private __reasonError = '';

  static styles = [
    nsFormFieldStyles,
    ...super.styles,
    css`
      textarea {
        width: 100%;
        min-height: 5em;
      }

      .instructions {
        white-space: pre-wrap;
      }
    `,
  ];

  private __toggle(key: string, on: boolean) {
    const order = checklistItems(configOf(this.job)).map((item) => item.key);
    const next = on ? [...this.__checked, key] : this.__checked.filter((k) => k !== key);
    this.__checked = order.filter((k) => next.includes(k));
    this.__errors = [];
  }

  private async __send(outcome: 'done' | 'cannotComplete') {
    const config = configOf(this.job);
    if (outcome === 'done') {
      this.__errors = checklistErrors(config, this.__checked, this.__confirmed);
      if (this.__errors.length > 0) return;
    } else if (!this.__reason.trim()) {
      this.__reasonError = 'Say why it can’t be done.';
      return;
    }
    const answers =
      outcome === 'done'
        ? { checked: this.__checked, ...(config.confirm ? { confirmed: this.__confirmed } : {}) }
        : { reason: this.__reason.trim() };
    try {
      this.loading(outcome === 'done' ? 'Marking it done' : 'Sending');
      await this.outcome(outcome, { ANSWERS: answers });
      this.loaded();
    } catch (err) {
      this.loaded();
      this.failJob(err, outcome === 'done' ? 'Unable to mark it done' : 'Unable to send that');
    }
  }

  renderActions() {
    if (this.__stuck) {
      return html`
        <wa-button variant="brand" appearance="outlined" type="button" @click=${() => (this.__stuck = false)}>
          Back
        </wa-button>
        <wa-button variant="brand" type="button" @click=${() => this.__send('cannotComplete')}>Send</wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" appearance="outlined" type="button" @click=${() => (this.__stuck = true)}>
        Can’t complete
      </wa-button>
      <wa-button variant="brand" type="button" @click=${() => this.__send('done')}>Done</wa-button>
    `;
  }

  render() {
    const config = configOf(this.job);
    const { instructions, confirm } = config;
    if (this.__stuck) {
      return html`
        <ns-job-layout>
          <div class="job-panel">
            <h3>Can’t complete this</h3>
            ${renderFormField({
              id: 'checklist-reason',
              label: 'Why',
              control: html`
                <textarea
                  id="checklist-reason"
                  maxlength="10000"
                  .value=${live(this.__reason)}
                  @input=${(e: Event) => {
                    this.__reason = (e.target as HTMLTextAreaElement).value;
                    this.__reasonError = '';
                  }}
                ></textarea>
              `,
              error: this.__reasonError || undefined,
            })}
          </div>
        </ns-job-layout>
      `;
    }
    return html`
      <ns-job-layout>
        ${typeof instructions === 'string' && instructions
          ? html`
              <p slot="intro" class="instructions">${instructions}</p>
            `
          : nothing}
        <div class="job-panel" role="group" aria-label="Checklist">
          ${checklistItems(config).map(
            (item) => html`
              <wa-checkbox
                class="job-option"
                .checked=${this.__checked.includes(item.key)}
                @change=${(e: Event) => this.__toggle(item.key, (e.target as HTMLInputElement).checked)}
              >
                ${item.label}
              </wa-checkbox>
            `,
          )}
          ${typeof confirm === 'string' && confirm
            ? html`
                <wa-checkbox
                  .checked=${this.__confirmed}
                  @change=${(e: Event) => {
                    this.__confirmed = (e.target as HTMLInputElement).checked;
                    this.__errors = [];
                  }}
                >
                  ${confirm}
                </wa-checkbox>
              `
            : nothing}
          ${this.__errors.map(
            (error) => html`
              <div class="field-error" role="alert">${error}</div>
            `,
          )}
        </div>
      </ns-job-layout>
    `;
  }
}
